import React, { useEffect, useMemo, useState } from "react";
import { AdminSidebar } from "./AdminDashboardPage";
import AdminHeader from "./AdminHeader";
import "../../css/adminReview.css";
// Table.jsx lives at src/components/Table.jsx
import { Table } from "../../components/Table";
import { Check, RefreshCw, Star, X } from "lucide-react";

import {
  getAllReviews,
  approveReview,
  rejectReview,
  replyReview,
  deleteReview,
} from "../../services/reviewService";
import { useToast } from "../../contexts/ToastContext";

const AdminReviewPage = () => {
  const { addToast, confirm, prompt } = useToast();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const res = await getAllReviews();
      setReviews(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("ERROR:", err);
      console.error("ERROR RESPONSE:", err?.response);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveReview(id);
      loadReviews();
    } catch (err) {
      console.error(err);
      addToast("Unable to approve review", "error");
    }
  };

  const handleReject = async (id) => {
    try {
      await rejectReview(id);
      loadReviews();
    } catch (err) {
      console.error(err);
      addToast("Unable to reject review", "error");
    }
  };

  const handleReply = async (id) => {
    const reply = await prompt({
      title: 'Reply to review',
      message: 'This reply is shown to the customer.',
      placeholder: 'Enter reply',
      confirmLabel: 'Send reply'
    });

    if (!reply?.trim()) {
      addToast('Enter a reply before sending', 'error');
      return;
    }

    try {
      await replyReview(id, reply);
      loadReviews();
    } catch (err) {
      console.error(err);
      addToast("Unable to send reply", "error");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete this review?',
      message: 'The review will be removed from the storefront.',
      confirmLabel: 'Delete'
    });
    if (!ok) return;

    try {
      await deleteReview(id);
      loadReviews();
    } catch (err) {
      console.error(err);
      addToast("Unable to delete review", "error");
    }
  };

  const stars = (rating) => {
    const filled = Math.max(1, Math.min(5, Number(rating) || 1));
    return (
      <span className="arv-stars" title={`${filled} out of 5`} aria-label={`${filled} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            size={13}
            strokeWidth={1.75}
            className={n <= filled ? 'arv-star is-filled' : 'arv-star'}
          />
        ))}
      </span>
    );
  };

  // Table.jsx's Cell only receives the row's data, not its index, so the
  // serial number is baked into the row objects up front instead.
  const rows = useMemo(
    () => (Array.isArray(reviews) ? reviews : []).map((r, i) => ({ ...r, _serial: i + 1 })),
    [reviews]
  );

  const columns = useMemo(
    () => [
      { Header: '#', accessor: '_serial', disableSortBy: true },
      {
        Header: 'Customer',
        accessor: 'customerName',
        Cell: ({ row }) => <span className="arv-customer">{row.original.customerName || 'Anonymous'}</span>
      },
      {
        Header: 'Product',
        id: 'product',
        accessor: (r) => r.product?.name || '-',
        Cell: ({ row }) => (
          <span className="arv-product" title={row.original.product?.name || '-'}>
            {row.original.product?.name || '-'}
          </span>
        )
      },
      {
        Header: 'Rating',
        id: 'rating',
        accessor: (r) => r.rating || 0,
        Cell: ({ row }) => stars(row.original.rating)
      },
      {
        Header: 'Title',
        id: 'title',
        accessor: (r) => r.title || '-',
        Cell: ({ row }) => <span className="arv-title">{row.original.title || '—'}</span>
      },
      {
        Header: 'Photos',
        id: 'photos',
        disableSortBy: true,
        accessor: (r) => (r.images || []).length,
        Cell: ({ row }) =>
          row.original.images?.length ? (
            <span className="arv-photos">
              {row.original.images.map((src) => (
                <a key={src} href={src} target="_blank" rel="noreferrer" title="Open photo">
                  <img src={src} alt="" className="arv-photo" />
                </a>
              ))}
            </span>
          ) : (
            <span className="arv-muted">—</span>
          )
      },
      {
        Header: 'Review',
        id: 'comment',
        disableSortBy: true,
        accessor: (r) => r.comment || '',
        Cell: ({ row }) => <span className="arv-comment">{row.original.comment || '—'}</span>
      },
      {
        Header: 'Status',
        id: 'status',
        accessor: (r) => r.status || '',
        Cell: ({ row }) => (
          <span className={`arv-status arv-status-${(row.original.status || '').toLowerCase()}`}>
            {row.original.status || 'Pending'}
          </span>
        )
      },
      {
        Header: 'Reply',
        id: 'reply',
        disableSortBy: true,
        accessor: (r) => r.adminReply || '-',
        Cell: ({ row }) => (
          <span className="arv-reply">{row.original.adminReply || '—'}</span>
        )
      },
      {
        Header: "Verified",
        id: "verifiedPurchase",
        accessor: (r) => (r.verifiedPurchase ? "Yes" : "No"),
        Cell: ({ row }) => (
          row.original.verifiedPurchase ? (
            <span className="verified-badge"><Check size={12} strokeWidth={2.5} /> Yes</span>
          ) : (
            <span className="not-verified-badge"><X size={12} strokeWidth={2.5} /> No</span>
          )
        )
      },
      {
        Header: 'Date',
        id: 'date',
        accessor: (r) => (r.createdAt ? new Date(r.createdAt).getTime() : 0),
        Cell: ({ row }) => (row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : '—')
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        accessor: () => null,
        Cell: ({ row }) => {
          const review = row.original;
          return (
            <div className="arv-actions">
              <button
                className="arv-btn-approve"
                onClick={() => handleApprove(review._id)}
                disabled={review.status === "Approved"}
                title="Approve review"
              >
                Approve
              </button>

              <button
                className="arv-btn-reject"
                onClick={() => handleReject(review._id)}
                disabled={review.status === "Rejected"}
                title="Reject review"
              >
                Reject
              </button>

              <button
                className="arv-btn-reply"
                onClick={() => handleReply(review._id)}
                title="Reply to review"
              >
                Reply
              </button>

              <button
                className="arv-btn-delete"
                onClick={() => handleDelete(review._id)}
                title="Delete review"
              >
                Delete
              </button>
            </div>
          );
        }
      }
    ],
    []
  );

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Reviews" />

        <div className="admin-review-page">
          <div className="arv-header">
            <div>
              <h1>Customer Reviews</h1>
              <p className="arv-subtitle">Manage, approve, reply, and moderate customer product reviews</p>
            </div>
            <div className="arv-header-actions">
              <span className="arv-count-pill">
                {reviews.length} {reviews.length === 1 ? 'Review' : 'Reviews'}
              </span>
              <button className="arv-refresh-btn" onClick={loadReviews} title="Refresh reviews">
                <RefreshCw size={15} />
                Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div className="arv-loading">
              <div className="arv-spinner" />
              <span>Loading reviews...</span>
            </div>
          ) : reviews.length === 0 ? (
            <div className="arv-empty">No Reviews Found</div>
          ) : (
            <Table columns={columns} data={rows} show />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminReviewPage;
